// The SAFed tests page (Session BDC): every lift with a SAFed recommendation, quotation, booked job or finished result, grouped by where
// it stands. It reads /api/safed-flow/worklist, which is built on the same lifecycle as the Compliance page's SAFed section (one rule for
// "where does this test stand", never a second query). A lift with nothing recorded is "not assessed" and is never listed as needing
// attention. Nothing here writes; raising a job opens the same form the lift page uses.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { RaiseSafedTestModal } from "./RaiseSafedTestModal";
import { SAFED_WORKLIST_LABEL_VIEW, SAFED_WORKLIST_ORDER_VIEW, type SafedWorklistItemView } from "../../lib/safedFlowTypes";

const selectCls = "min-h-11 rounded-ele border border-ele-purple-accent bg-ele-field px-2 text-sm text-ele-strong";

export function SafedTestsPage() {
  const [items, setItems] = useState<SafedWorklistItemView[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [failed, setFailed] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [raising, setRaising] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let live = true;
    api.safedFlow
      .worklist({})
      .then((r) => {
        if (!live) return;
        setItems(r.items);
        setTruncated(r.truncated);
        setFailed(false);
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [reloadKey]);

  const customers = useMemo(() => {
    const m = new Map<string, string>();
    for (const i of items ?? []) if (i.customerId) m.set(i.customerId, i.customerName ?? i.customerId);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [items]);
  const sites = useMemo(() => {
    const m = new Map<string, string>();
    for (const i of items ?? []) if (i.siteId && (!customerId || i.customerId === customerId)) m.set(i.siteId, i.siteName ?? i.siteId);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [items, customerId]);

  const shown = (items ?? []).filter((i) => (!customerId || i.customerId === customerId) && (!siteId || i.siteId === siteId));
  // One row per lift per stage: the tests on a lift at that stage, then the link to the lift's SAFed panel (and the job when booked).
  const groups = SAFED_WORKLIST_ORDER_VIEW.map((state) => ({
    state,
    rows: shown
      .map((lift) => ({ lift, tests: lift.tests.filter((t) => t.state === state) }))
      .filter((r) => r.tests.length > 0),
  }));

  return (
    <section>
      <PageHeader
        title="SAFed tests"
        subtitle="Supplementary testing of lifts, from the recommendation to the result. A lift with nothing recorded is not assessed and is not listed."
        actions={
          <Button variant="secondary" size="sm" className="min-h-11" onClick={() => setRaising(true)}>
            Raise a SAFed test
          </Button>
        }
      />
      {failed && <p role="alert" className="mt-4 text-sm text-ele-danger">Could not load the SAFed tests just now.</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <label className="text-sm text-ele-body">
          <span className="sr-only">Customer</span>
          <select className={selectCls} value={customerId} onChange={(e) => { setCustomerId(e.target.value); setSiteId(""); }}>
            <option value="">All customers</option>
            {customers.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
        </label>
        <label className="text-sm text-ele-body">
          <span className="sr-only">Site</span>
          <select className={selectCls} value={siteId} onChange={(e) => setSiteId(e.target.value)}>
            <option value="">All sites</option>
            {sites.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
        </label>
      </div>
      {!items && !failed && <Loader className="mt-4" />}
      {items && shown.length === 0 && (
        <p className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-4 text-sm text-ele-body">
          No SAFed work is recorded{customerId || siteId ? " with those filters" : " yet"}. Record a recommendation or a quotation on a lift&apos;s page, or raise a SAFed test.
        </p>
      )}
      {groups.map((g) =>
        g.rows.length === 0 ? null : (
          <section key={g.state} aria-label={SAFED_WORKLIST_LABEL_VIEW[g.state]} className="mt-6" data-testid={`safed-group-${g.state}`}>
            <h2 className="font-heading text-lg text-ele-strong">
              {SAFED_WORKLIST_LABEL_VIEW[g.state]} <span className="text-sm text-ele-muted">({g.rows.length})</span>
            </h2>
            <table className="ele-list-table mt-2 w-full text-sm">
              <thead>
                <tr className="text-left text-ele-muted">
                  <th>Lift</th>
                  <th>Site and customer</th>
                  <th>Tests</th>
                  <th>{g.state === "quoted" || g.state === "accepted" ? "Quotation" : "Job"}</th>
                </tr>
              </thead>
              <tbody>
                {g.rows.map(({ lift, tests }) => (
                  <tr key={lift.assetId}>
                    <td><Link className="text-ele-blue hover:underline" to={`/office/lifts/${lift.assetId}#safed`}>{lift.liftRef ?? "Lift"}</Link></td>
                    <td>{lift.siteName} · {lift.customerName}</td>
                    <td>{tests.map((t) => `${t.testCode} ${t.name}`).join("; ")}</td>
                    <td>
                      {tests.filter((t) => t.openJobId).map((t) => (
                        <Link key={t.testCode} className="mr-2 text-ele-blue hover:underline" to={`/office/jobsheets/${t.openJobId}`}>Open the job</Link>
                      ))}
                      {[...new Set(tests.map((t) => t.quoteRef).filter(Boolean))].join(", ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ),
      )}
      {truncated && <p className="mt-3 text-xs text-ele-muted">Only the first lifts are shown. Filter by customer or site to see the rest.</p>}
      {raising && (
        <RaiseSafedTestModal
          customerId={customerId || null}
          siteId={siteId || null}
          onClose={() => setRaising(false)}
          onRaised={() => {
            setRaising(false);
            setReloadKey((k) => k + 1);
          }}
        />
      )}
    </section>
  );
}
